import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Platform } from '@ionic/angular';

const PENDING_DEEP_LINK_KEY = 'pendingDeepLink';

// Settings deep link aliases: deep-link-key → angular-route-segment
const SETTINGS_ROUTES: { [key: string]: string } = {
  'profile': 'profile',
  'general': 'general-settings',
  'access': 'access-settings',
  'lighting': 'lighting',
  'climate': 'climate',
  'spa': 'shower',
  'notifications': 'notification-settings',
  'early-access': 'early-access',
};

export interface PendingDeepLink {
  route: string;
  params: { [key: string]: string };
}

@Injectable({
  providedIn: 'root'
})
export class DeepLinkService {
  constructor(
    private router: Router,
    private zone: NgZone,
    private platform: Platform
  ) {}

  initializeDeepLinks() {
    console.log('[DeepLink] Initializing deep links, platform is capacitor:', this.platform.is('capacitor'));
    if (this.platform.is('capacitor')) {
      App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        console.log('[DeepLink] appUrlOpen event received:', event.url);
        this.zone.run(() => {
          const url = new URL(event.url);
          this.handleDeepLink(url);
        });
      });
      console.log('[DeepLink] appUrlOpen listener registered');
    }
  }

  private handleDeepLink(url: URL) {
    console.log('[DeepLink] =============================');
    console.log('[DeepLink] URL received:', url.href);
    console.log('[DeepLink] pathname:', url.pathname);
    console.log('[DeepLink] host:', url.host);

    const path = url.pathname.replace(/^\//, '');
    console.log('[DeepLink] Parsed path:', path);
    const queryParams = Object.fromEntries(url.searchParams);

    // Check if this is a settings sub-route (e.g., settings/climate)
    // Note: Settings routes are nested under /dashboard/settings/
    if (path.startsWith('settings/')) {
      const settingsKey = path.replace('settings/', '');
      const settingsSegment = SETTINGS_ROUTES[settingsKey];
      console.log('[DeepLink] Settings route - key:', settingsKey, 'segment:', settingsSegment);

      if (settingsSegment) {
        const route = `/dashboard/settings/${settingsSegment}`;
        console.log('[DeepLink] Navigating to settings route:', route);
        this.storePendingDeepLink(route, queryParams);
        this.router.navigate([route], { queryParams }).then(
          (success) => console.log('[DeepLink] Navigation result:', success),
          (error) => console.error('[DeepLink] Navigation error:', error)
        );
        return;
      } else {
        console.log('[DeepLink] Unknown settings key:', settingsKey);
      }
    }

    const routeMapping: { [key: string]: { route: string; params?: { [key: string]: string } } } = {
      'dashboard': { route: '/dashboard' },
      'settings': { route: '/dashboard/settings' },
      'intercom': { route: '/intercom' },
      'home': { route: '/dashboard' },
      'tours': { route: '/tours' },
      'tour': { route: '/tours' },
      'early-access': { route: '/dashboard/settings/early-access' },
      'holidays-2025': { route: '/dashboard', params: { showHoliday: 'true' } },
    };

    const mapping = routeMapping[path];
    const route = mapping?.route || `/${path}`;
    const mergedParams = { ...queryParams, ...(mapping?.params || {}) };

    if (this.isValidRoute(route)) {
      // Store pending deep link - will be used after authentication
      this.storePendingDeepLink(route, mergedParams);
      // Try to navigate (will be blocked by auth guard if not authenticated)
      this.router.navigate([route], { queryParams: mergedParams });
    } else {
      console.warn('Unknown deep link path:', path);
      this.router.navigate(['/dashboard']);
    }
  }

  private isValidRoute(route: string): boolean {
    const validRoutes = [
      '/dashboard',
      '/dashboard/settings',
      '/intercom',
      '/signin',
      '/intro',
      '/tours'
    ];

    // Check exact match first
    if (validRoutes.includes(route)) {
      return true;
    }

    // Check if it's a valid settings sub-route (nested under /dashboard/settings/)
    if (route.startsWith('/dashboard/settings/')) {
      const settingsSegment = route.replace('/dashboard/settings/', '');
      return Object.values(SETTINGS_ROUTES).includes(settingsSegment);
    }

    return false;
  }

  checkInitialUrl() {
    console.log('[DeepLink] Checking initial/launch URL...');
    App.getLaunchUrl().then((result) => {
      console.log('[DeepLink] getLaunchUrl result:', result);
      if (result && result.url) {
        const url = new URL(result.url);
        this.handleDeepLink(url);
      } else {
        console.log('[DeepLink] No launch URL found');
      }
    }).catch((error) => {
      console.error('[DeepLink] Error getting launch URL:', error);
    });
  }

  /**
   * Store a pending deep link to be processed after authentication
   * Uses both sessionStorage and localStorage for reliability on iOS
   */
  storePendingDeepLink(route: string, params: { [key: string]: string }): void {
    const pendingLink: PendingDeepLink = { route, params };
    const json = JSON.stringify(pendingLink);
    sessionStorage.setItem(PENDING_DEEP_LINK_KEY, json);
    localStorage.setItem(PENDING_DEEP_LINK_KEY, json);
    console.log('[DeepLink] Stored pending deep link:', pendingLink);
  }

  /**
   * Get and clear any pending deep link
   * Checks both sessionStorage and localStorage for reliability
   */
  getPendingDeepLink(): PendingDeepLink | null {
    // Try sessionStorage first, fall back to localStorage
    let stored = sessionStorage.getItem(PENDING_DEEP_LINK_KEY);
    if (!stored) {
      stored = localStorage.getItem(PENDING_DEEP_LINK_KEY);
    }
    if (stored) {
      sessionStorage.removeItem(PENDING_DEEP_LINK_KEY);
      localStorage.removeItem(PENDING_DEEP_LINK_KEY);
      try {
        return JSON.parse(stored) as PendingDeepLink;
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Check if there's a pending deep link and navigate to it
   * Call this after successful authentication
   */
  processPendingDeepLink(): boolean {
    console.log('[DeepLink] Processing pending deep link...');
    const pending = this.getPendingDeepLink();
    console.log('[DeepLink] Retrieved pending:', pending);
    if (pending) {
      // Process if there's a non-dashboard route or special params
      const hasSpecialRoute = pending.route !== '/dashboard';
      const hasParams = pending.params && Object.keys(pending.params).length > 0;
      console.log('[DeepLink] hasSpecialRoute:', hasSpecialRoute, 'hasParams:', hasParams);

      if (hasSpecialRoute || hasParams) {
        console.log('[DeepLink] Navigating to pending route:', pending.route);
        this.router.navigate([pending.route], {
          queryParams: pending.params,
          replaceUrl: true
        }).then(
          (success) => console.log('[DeepLink] Pending navigation result:', success),
          (error) => console.error('[DeepLink] Pending navigation error:', error)
        );
        return true;
      }
    }
    console.log('[DeepLink] No pending deep link to process');
    return false;
  }
}
